import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect } from "react";
import styles from "./MultipleImageUpload.module.css";
import FilePreview from "./FilePreview";
import FileForm from "./FileForm";
import ValidationService from "../../Services/validationService";
import FileService from "../../Services/fileService";
import ImageTool from "../../Tools/ImageTools";
import { FaTimes } from "react-icons/fa";
import { useToasts } from "../../LayoutComponents/ToastContext";
import i18n from "../../Services/i18n";
import { useEditLock } from "./EditLockContext";

const MAX_FILES = 20;

const MultipleImageUpload = forwardRef(({ onDirtyChange }, ref) => {
  const [dragCount, setDragCount] = useState(0);
  const [images, setImages] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const inputRef = useRef();
  const { addToast } = useToasts();
  const { isLocked } = useEditLock();

  const notifyDirtyChange = () => {
    onDirtyChange?.();
  };

  useEffect(() => {
    notifyDirtyChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images]);

  useImperativeHandle(ref, () => ({
    save: async () => {
      setIsUploading(true);
      const updated = [...images];
      const results = [];

      for (let i = 0; i < updated.length; i++) {
        updated[i].status = "uploading";
        setImages([...updated]);

        try {
          const processedBlob = await ImageTool.applyImageEdits(
            updated[i].file,
            updated[i].editData,
          );

          const processed = new File(
            [processedBlob],
            updated[i].file.name.replace(/\.[^/.]+$/, ".jpg"),
            { type: "image/jpeg" },
          );

          const thumbnail = await ImageTool.getImageThumbnail(processed);
          const adjusted = await ImageTool.compressToTargetSize(processed);

          const date =
            updated[i].form.selectedDate === "createDate"
              ? updated[i].form.createDate
              : updated[i].form.selectedDate === "modifyDate"
                ? updated[i].form.modifyDate
                : new Date();

          const result = await FileService.uploadFile(
            adjusted,
            thumbnail,
            date,
            updated[i].form.filename,
            updated[i].form.tags,
          );

          if (result.success) {
            updated[i].status = "done";
            results.push(result.id);
          } else {
            updated[i].status = "error";
          }

          updated[i].status = result.success ? "done" : "error";
        } catch {
          updated[i].status = "error";
        }

        setImages([...updated]);
      }

      setIsUploading(false);

      return {
        success: updated.every((img) => img.status === "done"),
        id: results,
      };
    },

    isDirty: () => {
      return images.length > 0;
    },
  }));

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    setDragCount((count) => count + 1);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragCount((count) => Math.max(0, count - 1));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragCount(0);

    const files = Array.from(e.dataTransfer.files).filter((f) => f.type.startsWith("image/"));

    if (files.length) {
      const event = { target: { files } };
      onFileChange(event);
    }
  };

  const onFileChange = (e) => {
    const selected = Array.from(e.target.files).filter((f) => f.type.startsWith("image/"));

    if (images.length + selected.length > MAX_FILES) {
      addToast(
        i18n.t("FileUploaderModal.MultipleFile.TooManyFiles"),
        "TOOMANYFILES",
        "ERROR",
        true,
      );
      return;
    }

    const validated = selected
      .map((file) => {
        const isValid = ValidationService.IsFileUploadValid(file);
        if (!isValid.isValid) return null;

        const baseForm = {
          filename: FileService.fileNameWithoutExtension(file.name),
          tags: "",
          selectedDate: "",
          createDate: file.lastModifiedDate ? new Date(file.lastModifiedDate) : null,
          modifyDate: file.lastModified ? new Date(file.lastModified) : null,
        };

        return {
          file,
          preview: URL.createObjectURL(file),
          form: baseForm,
          status: "pending",
          editData: {
            crop: null,
            rotation: 0,
            textOverlays: [],
          },
        };
      })
      .filter(Boolean);

    if (validated.length === 0) return;

    setImages((prev) => [...prev, ...validated]);

    if (selectedIndex === null && validated.length > 0) {
      setSelectedIndex(0);
    }
  };

  const handleFormChange = (form) => {
    if (selectedIndex === null) return;

    setImages((prevImages) => {
      const updated = [...prevImages];
      updated[selectedIndex] = {
        ...updated[selectedIndex],
        form: {
          ...updated[selectedIndex].form,
          ...form,
        },
      };
      return updated;
    });
  };

  const handleDeleteIndex = (index) => {
    setDeletingIndex(index);

    setTimeout(() => {
      const newImages = images.filter((_, i) => i !== index);
      setImages(newImages);
      setDeletingIndex(null);

      if (index === selectedIndex) {
        if (newImages.length === 0) {
          setSelectedIndex(null);
        } else {
          setSelectedIndex(Math.max(0, index - 1));
        }
      } else if (index < selectedIndex) {
        setSelectedIndex((prev) => prev - 1);
      }
    }, 200);
  };

  return (
    <div className={styles.editorRow}>
      <div className={styles.leftColumn}>
        <div
          className={`${styles.dropZone} ${dragCount > 0 ? styles.dragOver : ""}`}
          onClick={() => inputRef.current.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}>
          <p>Cliquer ou glisser-déposer des images (max {MAX_FILES})</p>
          <input
            type="file"
            accept="image/*"
            multiple
            ref={inputRef}
            onChange={onFileChange}
            style={{ display: "none" }}
          />
        </div>

        {images.length > 0 && (
          <div className={`${styles.thumbnailGrid} ${isLocked ? styles.locked : ""}`}>
            {images.map((img, index) => (
              <div
                key={index}
                className={`${styles.thumbnailWrapper} ${
                  index === selectedIndex ? styles.active : ""
                } ${index === deletingIndex ? styles.thumbnailDeleting : ""}`}>
                <div className={styles.thumbnail}>
                  <img
                    src={img.preview}
                    alt={`thumb-${index}`}
                    onClick={() => {
                      if (!isLocked) setSelectedIndex(index);
                    }}
                  />

                  {isUploading && (
                    <div className={styles.statusOverlay}>
                      {img.status === "uploading" && <span>⏳</span>}
                      {img.status === "done" && <span>✅</span>}
                      {img.status === "error" && <span>❌</span>}
                      {img.status === "pending" && <span>🔲</span>}
                    </div>
                  )}

                  <button
                    className={styles.deleteThumbBtn}
                    onClick={() => handleDeleteIndex(index)}>
                    <FaTimes />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedIndex !== null && images[selectedIndex] && (
        <>
          <div className={styles.centerColumn}>
            <div className={styles.preview}>
              <FilePreview
                file={images[selectedIndex].file}
                preview={images[selectedIndex].preview}
                editable={true}
                editData={images[selectedIndex].editData}
                onEditChange={(data) => {
                  const newImages = [...images];
                  newImages[selectedIndex] = {
                    ...newImages[selectedIndex],
                    editData: { ...data },
                  };
                  setImages(newImages);
                }}
                onResetEdit={() => {
                  const newImages = [...images];
                  newImages[selectedIndex] = {
                    ...newImages[selectedIndex],
                    editData: {
                      crop: null,
                      rotation: 0,
                      textOverlays: [],
                    },
                  };
                  setImages(newImages);
                }}
              />
            </div>
          </div>

          <div className={styles.rightColumn}>
            <FileForm
              formState={images[selectedIndex].form}
              onChange={handleFormChange}
              isImage={true}
            />
          </div>
        </>
      )}
    </div>
  );
});

export default MultipleImageUpload;
